<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="page">
    <!-- 顶部导航区：使用 examPage 背景 -->
    <div class="header">
      <div class="back" @click="handleBack">
        <img src="@/assets/examPage/back.png" alt="" />
      </div>
      <div class="header-title">首次测评</div>
    </div>

    <!-- 测评内容区 -->
    <Transition name="page" appear>
      <div class="box">
      <div class="exam-content">
        <!-- 顶部提示 -->
        <div class="hint-bar">
          <img class="tit-left" src="@/assets/examPage/tit-left.png" alt="" />
          <span class="hint-text"
            >测评时间及测评题量将根据您测评过程智能动态调整，请您耐心完成测评哦</span
          >
          <img class="tit-right" src="@/assets/examPage/tit-right.png" alt="" />
        </div>

        <!-- 左右分栏：左侧说明+单词，中间虚线，右侧选项 -->
        <div class="main-content">
          <div class="left-column">
            <div class="instruction">
              <span class="instruction-bar"></span>
              <span>请选择您认为最准确或最接近的释义</span>
            </div>
            <div class="word-area">
              {{ currentQuestion.word }}
            </div>
          </div>
          <div class="divider"></div>
          <div class="right-column">
            <div class="options-area">
              <div
                v-for="(opt, idx) in currentQuestion.options"
                :key="idx"
                :style="{ animationDelay: `${idx * 0.06}s` }"
                :class="[
                  'option-item',
                  {
                    correct: selectedAnswer === idx && isCorrect(idx),
                    wrong: selectedAnswer === idx && isWrong(idx),
                    unknown: selectedAnswer === idx && isUnknown(idx),
                  },
                ]"
                @click="selectAnswer(idx)"
              >
                <Transition name="opt-icon">
                  <img
                    v-if="selectedAnswer === idx && isCorrect(idx)"
                    class="opt-icon"
                    src="@/assets/examPage/right-icon.png"
                    alt=""
                  />
                </Transition>
                <Transition name="opt-icon">
                  <img
                    v-if="selectedAnswer === idx && isWrong(idx)"
                    class="opt-icon"
                    src="@/assets/examPage/error-icon.png"
                    alt=""
                  />
                </Transition>
                <span class="opt-letter">{{ opt.letter }}</span>
                <span v-if="opt.partOfSpeech" class="opt-divider"></span>
                <span v-if="opt.partOfSpeech" class="opt-pos">{{
                  opt.partOfSpeech
                }}</span>
                <span class="opt-meaning">{{ opt.meaning }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 右下角铅笔装饰 -->
        <img class="pen-deco" src="@/assets/examPage/pen.png" alt="" />
      </div>
    </div>
    </Transition>

    <!-- Toast 提示 -->
    <Transition name="toast">
      <div v-if="toastVisible" class="toast">
        <img
          v-if="toastType === 'correct'"
          class="toast-icon"
          src="@/assets/examPage/right-icon.png"
          alt=""
        />
        <img
          v-if="toastType === 'wrong'"
          class="toast-icon"
          src="@/assets/examPage/error-icon.png"
          alt=""
        />
        <span class="toast-text">{{ toastText }}</span>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { setDesignSize } from "@/utils/adapt.js";
import { submitPreciseAnswer } from "@/api/precise.js";

const router = useRouter();

onMounted(() => {
  setDesignSize(960, 600);
  questionStartAt.value = Date.now();
  const saved = localStorage.getItem("preciseExamInit");
  if (saved) {
    try {
      examInitData.value = JSON.parse(saved);
    } catch (e) {
      console.error("解析初始化测评数据失败:", e);
    }
  }
});

onBeforeUnmount(() => {
  window.dispatchEvent(new Event("resize"));
});

const examQuestions = ref([]);

const selectedAnswer = ref(null);
const toastVisible = ref(false);
const toastType = ref(null);
const isSubmitting = ref(false);
const questionStartAt = ref(Date.now());
const examInitData = ref(null);
let toastTimer = null;

const currentTopic = computed(() => examInitData.value?.newsTopic ?? null);

function normalizeTopicToQuestion(topic) {
  if (!topic) return null;
  const options = Array.isArray(topic.options) ? topic.options : [];
  if (options.length === 0) return null;
  const mappedOptions = options.map((opt, index) => ({
    optionId: opt.optionId ?? String(index + 1),
    letter: opt.options || String.fromCharCode(65 + index),
    partOfSpeech: "",
    meaning: opt.content || "",
    imageUrl: opt.imageUrl || "",
    correct: String(opt.correct ?? "0"),
  }));
  const correct = mappedOptions.findIndex((opt) => String(opt.correct) === "1");
  return {
    word: topic.wordName || topic.wordInfo?.name || "",
    options: mappedOptions,
    correct: correct >= 0 ? correct : -1,
  };
}

const currentQuestion = computed(() => {
  const topicQuestion = normalizeTopicToQuestion(currentTopic.value);
  if (topicQuestion) return topicQuestion;
  const list = examQuestions.value;
  return list[0] ?? { word: "", options: [], correct: -1 };
});

const correctIndex = computed(
  () => currentQuestion.value?.correct ?? -1
);

const toastText = computed(() => {
  if (toastType.value === "correct") return "True";
  if (toastType.value === "wrong") return "False";
  if (toastType.value === "unknown") return "Continue";
  return "";
});

function isUnknown(idx) {
  const opt = currentQuestion.value?.options?.[idx];
  return String(opt?.correct ?? "") === "2" || opt?.meaning === "不认识" || opt?.meaning === "我不认识";
}

function isCorrect(idx) {
  return idx === correctIndex.value && !isUnknown(idx);
}

function isWrong(idx) {
  return (
    selectedAnswer.value === idx &&
    idx !== correctIndex.value &&
    !isUnknown(idx)
  );
}

async function selectAnswer(idx) {
  selectedAnswer.value = idx;
  const unknown = isUnknown(idx);
  const correct = isCorrect(idx);
  const passStatus = unknown ? "2" : correct ? "1" : "0";
  if (toastTimer) clearTimeout(toastTimer);
  if (correct) {
    toastType.value = "correct";
  } else if (!unknown) {
    toastType.value = "wrong";
  } else {
    toastType.value = "unknown";
  }
  toastVisible.value = true;
  toastTimer = setTimeout(() => {
    toastVisible.value = false;
    toastType.value = null;
  }, 2500);

  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    const topic = currentTopic.value || {};
    const wordInfo = topic.wordInfo || {
      wordId: "",
      name: currentQuestion.value?.word || "",
    };
    const options = Array.isArray(topic.options) && topic.options.length > 0
      ? topic.options.map((opt, i) => ({
          optionId: opt.optionId || String(i + 1),
          content: opt.content || "",
          options: opt.options || String.fromCharCode(65 + i),
          correct: String(opt.correct ?? 0),
          imageUrl: opt.imageUrl || "",
        }))
      : (currentQuestion.value?.options || []).map((opt, i) => ({
          optionId: opt.optionId || String(i + 1),
          content: opt.meaning || "",
          options: opt.letter || "",
          correct: String(opt.correct ?? (i === correctIndex.value ? 1 : 0)),
          imageUrl: opt.imageUrl || "",
        }));
    const answerTime = String(Math.max(0, Date.now() - questionStartAt.value));
    const selectedOpt = currentQuestion.value?.options?.[idx] || null;
    const answerType = Number(
      selectedOpt?.correct ?? (unknown ? 2 : correct ? 1 : 0)
    );
    const payload = {
      wordId: topic.wordId || wordInfo.wordId || "",
      wordInfo,
      passStatus,
      answerType,
      answerTime,
      options,
      questionId: topic.questionId || "",
      type: String(topic.type ?? 1),
      gameType: String(topic.gameType ?? 0),
      difficulty: String(topic.difficulty ?? ""),
      sort: String(topic.sort ?? 0),
      submitContext: [selectedOpt?.meaning || ""],
      contents: Array.isArray(topic.contents) && topic.contents.length > 0
        ? topic.contents
        : [currentQuestion.value?.word || ""],
      analysis: topic.analysis || "",
      source: String(topic.source ?? ""),
    };
    const res = await submitPreciseAnswer(payload);

    // 兼容后端返回：
    // 1) { status, newTopic }（用户贴的格式）
    // 2) { code, data: { status, newTopic } }（若封装在 data 内）
    const newTopic =
      res?.newTopic ??
      res?.data?.newTopic ??
      res?.data?.newsTopic ??
      null;

    if (newTopic) {
      examInitData.value = {
        ...(examInitData.value || {}),
        newsTopic: newTopic,
      };
      selectedAnswer.value = null;
    }

    // 提交答题完成判断：后端返回 status === 1 表示答题已结束
    const submitStatus =
      res?.status ??
      res?.data?.status ??
      res?.data?.data?.status ??
      null;
    if (submitStatus === 1) {
      router.push("/firstExam/firstReport");
      return;
    }
    questionStartAt.value = Date.now();
  } catch (error) {
    console.error("提交答题记录失败:", error);
  } finally {
    isSubmitting.value = false;
  }
}

function handleBack() {
  window.history.back();
}
</script>

<style scoped lang="scss">
@keyframes hint-fade-in {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes word-fade-in {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes option-fade-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.page {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  overflow-y: auto;
  background: url("@/assets/examPage/bg.png") no-repeat center top;
  background-size: cover;
  padding: 42px 30px 30px;
}

.header {
  display: flex;
  animation: hint-fade-in 0.4s ease both;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 15px;

  .back {
    position: absolute;
    left: 0;
    top: 0;
    img {
      width: 12px;
      height: 20px;
    }
  }

  .header-title {
    font-weight: bold;
    font-size: 19px;
    color: #ffffff;
    text-align: center;
  }
}

.page-enter-active,
.page-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.page-enter-to,
.page-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.box {
  position: relative;
  width: 900px;
  height: 487px;
  background: url("@/assets/examPage/content-bg.png") no-repeat center center;
  background-size: 100% 100%;
  padding: 7px 33px 20px 23px;
  box-sizing: border-box;
}

.exam-content {
  position: relative;

  .hint-bar {
    display: flex;
    animation: hint-fade-in 0.4s ease 0.05s both;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-left: -16px;
    margin-right: -26px;
    width: 886px;
    height: 35px;
    background: linear-gradient(
      90deg,
      rgba(244, 240, 255, 0) 0%,
      rgba(244, 240, 255, 0.5) 50.25%,
      rgba(244, 240, 255, 0) 100%
    );
    border-radius: 35px 35px 0px 0px;

    .tit-left,
    .tit-right {
      width: 9px;
      height: 4px;
    }

    .hint-text {
      font-size: 14px;
      color: #a07dff;
      line-height: 21px;
      text-align: center;
    }
  }

  .main-content {
    display: flex;
    align-items: flex-start;
    gap: 0;
  }

  .left-column {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .instruction {
    display: flex;
    animation: hint-fade-in 0.4s ease 0.1s both;
    align-items: center;
    gap: 5px;
    margin-top: 10px;

    .instruction-bar {
      width: 4px;
      height: 13px;
      background: #c0a8ff;
      border-radius: 2px;
    }

    span:last-child {
      font-size: 14px;
      color: #666666;
    }
  }

  .divider {
    width: 0;
    height: 425px;
    border-left: 1px dashed #e0e0e0;
    flex-shrink: 0;
    margin: 0 24px;
  }

  .right-column {
    flex: 1 1 0;
    min-width: 0;
  }

  .word-area {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    font-weight: bold;
    font-size: 45px;
    animation: word-fade-in 0.5s ease 0.15s both;
    color: #333333;
    line-height: 65px;
    margin-top: 136px;
  }

  .options-area {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 20px;
  }

  .option-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 392px;
    height: 55px;
    animation: option-fade-in 0.4s ease both;
    padding: 0 20px;
    font-size: 16px;
    color: #333;
    background: #f6f6f6;
    border-radius: 12px;
    border: 2px solid transparent;
    transition: background 0.35s ease, border-color 0.35s ease,
      border-width 0.2s ease, transform 0.2s ease;

    &.correct {
      background: #f4fffb;
      border: 1px solid #5ce3b9;
      color: #5ce3b9;

      .opt-letter,
      .opt-pos,
      .opt-meaning,
      .opt-divider {
        color: #5ce3b9;
      }

      .opt-divider {
        background: #5ce3b9;
      }
    }

    &.wrong {
      background: #fff5f5;
      border: 1px solid #ff9090;
      color: #ff9090;

      .opt-letter,
      .opt-pos,
      .opt-meaning,
      .opt-divider {
        color: #ff9090;
      }

      .opt-divider {
        background: #ff9090;
      }
    }

    &.unknown {
      background: #fff8f4;
      border: 1px solid #ffbd90;
      color: #ffbd90;

      .opt-letter,
      .opt-pos,
      .opt-meaning,
      .opt-divider {
        color: #ffbd90;
      }

      .opt-divider {
        background: #ffbd90;
      }
    }

    .opt-icon {
      position: absolute;
      top: -11.5px;
      right: -11.5px;
      width: 23px;
      height: 23px;
      z-index: 1;
    }
  }

  .opt-icon-enter-active,
  .opt-icon-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
  }

  .opt-icon-enter-from,
  .opt-icon-leave-to {
    opacity: 0;
    transform: scale(0.5);
  }

  .opt-icon-enter-to,
  .opt-icon-leave-from {
    opacity: 1;
    transform: scale(1);
  }

  .option-item {
    .opt-letter {
      font-weight: 400;
      font-size: 17px;
      color: #464646;
    }

    .opt-divider {
      width: 1px;
      height: 12px;
      background: #dddddd;
      border-radius: 1px;
      flex-shrink: 0;
    }

    .opt-pos {
      font-weight: 400;
      font-size: 15px;
      color: #464646;
    }

    .opt-meaning {
      flex: 1;
    }
  }

  .pen-deco {
    position: fixed;
    right: 10px;
    top: 472px;
    width: 70px;
    height: 78px;
    z-index: 1;
  }
}

.toast {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 10px;
  padding: 8px 15px;
  z-index: 100;

  .toast-icon {
    width: 19px;
    height: 19px;
    margin-right: 5px;
    flex-shrink: 0;
  }

  .toast-text {
    font-size: 18px;
    color: #ffffff;
    line-height: 25px;
  }
}

.toast-enter-active {
  transition: opacity 0.35s ease-out;
}

.toast-leave-active {
  transition: opacity 0.3s ease-in;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
}

.toast-enter-to,
.toast-leave-from {
  opacity: 1;
}
</style>
